import type { Category, TransactionType } from "@/context/FinanceContext";
import { PersistedState } from "./useFinanceState";
import { toast } from "@/hooks/use-toast";

export function useCategoriesOperations(
  state: PersistedState,
  setState: React.Dispatch<React.SetStateAction<PersistedState>>,
  user: any,
  createCategory: (data: any) => Promise<string>,
  updateSupabaseCategory: (id: string, data: any) => Promise<boolean>,
  deleteCategory: (id: string) => Promise<boolean>
) {
  const addCategory = async (
    name: string,
    color: string = "#64748B",
    type: TransactionType = "despesa",
    dreCategory?: string,
    hierarchy?: { level2Id?: string; code?: string }
  ): Promise<string | null> => {
    // Check for duplicate names
    const existingCategory = state.categories.find(c => c.name.toLowerCase() === name.toLowerCase());
    if (existingCategory) {
      toast({
        variant: "destructive",
        title: "Nome duplicado",
        description: "Já existe uma categoria com este nome.",
      });
      return null;
    }

    const tempId = `cat-${crypto.randomUUID()}`;
    
    // Sync to Supabase if user is logged in
    if (user) {
      try {
        const supabaseId = await createCategory({
          name,
          color,
          type,
          dre_category: dreCategory,
          level2_id: hierarchy?.level2Id,
          code: hierarchy?.code,
        });
        if (supabaseId) {
          // Update local state with the real ID from Supabase
          setState((s) => ({
            ...s,
            categories: [...s.categories, {
              id: supabaseId,
              name,
              color,
              type,
              dreCategory,
              level2Id: hierarchy?.level2Id,
              code: hierarchy?.code,
            }],
          }));
          return supabaseId;
        }
      } catch (error) {
        console.error('Error syncing category to Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível salvar a categoria na nuvem.",
        });
        return null;
      }
    } else {
      // Update local state with temp ID if not logged in
      setState((s) => ({
        ...s,
        categories: [...s.categories, {
          id: tempId,
          name,
          color,
          type,
          dreCategory,
          level2Id: hierarchy?.level2Id,
          code: hierarchy?.code,
        }],
      }));
      return tempId;
    }
    return null;
  };

  const updateCategory = async (id: string, name?: string, color?: string, type?: TransactionType, dreCategory?: string) => {
    // Update local state first
    setState((s) => ({
      ...s,
      categories: s.categories.map((c) => (
        c.id === id ? { 
          ...c, 
          ...(name !== undefined ? { name } : {}), 
          ...(color !== undefined ? { color } : {}), 
          ...(type !== undefined ? { type } : {}),
          ...(dreCategory !== undefined ? { dreCategory } : {})
        } : c
      )),
    }));

    // Sync to Supabase if user is logged in
    if (user) {
      try {
        await updateSupabaseCategory(id, {
          ...(name && { name }),
          ...(color && { color }),
          ...(type && { type }),
          ...(dreCategory !== undefined && { dre_category: dreCategory }),
        });
      } catch (error) {
        console.error('Error syncing category update to Supabase:', error);
      }
    }
  };


  const removeCategory = async (id: string): Promise<boolean> => {
    // Sync to Supabase if user is logged in first
    if (user) {
      try {
        const success = await deleteCategory(id);
        if (!success) {
          // Se a exclusão no Supabase falhou, não remove do estado local
          return false;
        }
        
        toast({
          title: "Categoria excluída",
          description: "A categoria foi removida com sucesso.",
        });
      } catch (error) {
        console.error('Error deleting category from Supabase:', error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Não foi possível excluir a categoria. Tente novamente.",
        });
        return false;
      }
    }

    // Update local state only after successful Supabase deletion (or if offline)
    setState((s) => ({
      ...s,
      categories: s.categories.filter((c) => c.id !== id),
    }));

    if (!user) {
      toast({
        title: "Categoria excluída localmente",
        description: "A categoria foi removida localmente. Faça login para sincronizar.",
      });
    }
    return true;
  };

  return {
    addCategory,
    updateCategory,
    removeCategory,
  };
}
